/**
 * Auth Store - SuperTokens Integration
 *
 * Zustand store for authentication state management using SuperTokens
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import * as auth from '../lib/auth';
import type { AuthUser } from '../lib/auth';

// API URL with fallback
const API_URL = import.meta.env.VITE_API_URL || 'https://api.rumio.world';

// Role types matching backend
export type Role = 'super_admin' | 'admin' | 'moderator' | 'support' | 'content_manager' | 'user';

export interface User {
  id: string;
  email: string;
  name?: string;
  subscription_tier: 'free' | 'pro' | 'pro_plus';
  subscription_source?: 'stripe' | 'apple' | 'google';
  role?: Role;
  is_admin?: boolean;
  referralCode?: string;
  avatarUrl?: string;
  created_at: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  permissions: string[];
  permissionsLoaded: boolean;
  _hasHydrated: boolean;
  _justLoggedOut: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  register: (email: string, password: string, name?: string, turnstileToken?: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  handleAuthCallback: () => Promise<void>;
  logout: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  initialize: () => Promise<void>;
  setHasHydrated: (state: boolean) => void;
  fetchPermissions: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  clearError: () => void;
}

// Helper to check if user has admin access
const checkIsAdmin = (user?: User | null): boolean => {
  if (!user) return false;
  // Check is_admin flag first (new area-based system)
  if (user.is_admin) return true;
  // Fall back to role check (legacy)
  return user.role !== undefined && user.role !== 'user';
};

// Helper to map AuthUser to our User type
function mapAuthUser(authUser: AuthUser): User {
  return {
    id: authUser.id,
    email: authUser.email,
    name: authUser.name,
    subscription_tier: (authUser.tier as 'free' | 'pro' | 'pro_plus') || 'free',
    role: authUser.role as Role,
    is_admin: authUser.role !== 'user',
    referralCode: authUser.referralCode || undefined,
    avatarUrl: authUser.avatarUrl || undefined,
    created_at: authUser.createdAt,
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      isAdmin: false,
      permissions: [],
      permissionsLoaded: false,
      _hasHydrated: false,
      _justLoggedOut: false,
      error: null,

      setHasHydrated: (state) => set({ _hasHydrated: state }),

      setUser: (user) => set({
        user,
        isAuthenticated: !!user,
        isAdmin: checkIsAdmin(user),
        isLoading: false
      }),

      // Login with email/password
      login: async (email: string, password: string, turnstileToken?: string) => {
        set({ isLoading: true, error: null });

        try {
          const { user } = await auth.signIn(email, password, turnstileToken);
          const mappedUser = mapAuthUser(user);
          const newIsAdmin = checkIsAdmin(mappedUser);

          set({
            user: mappedUser,
            isAuthenticated: true,
            isAdmin: newIsAdmin,
            isLoading: false,
          });

          // Fetch permissions if admin
          if (newIsAdmin) {
            get().fetchPermissions();
          }
        } catch (error) {
          const err = error as Error;
          set({
            isLoading: false,
            error: err.message || 'Login failed',
          });
          throw error;
        }
      },

      // Register with email/password
      register: async (email: string, password: string, name?: string, turnstileToken?: string) => {
        set({ isLoading: true, error: null });

        try {
          const { user } = await auth.signUp(email, password, name, turnstileToken);
          const mappedUser = mapAuthUser(user);

          set({
            user: mappedUser,
            isAuthenticated: true,
            isAdmin: false,
            isLoading: false,
          });
        } catch (error) {
          const err = error as Error;
          set({
            isLoading: false,
            error: err.message || 'Registration failed',
          });
          throw error;
        }
      },

      // Login with Google OAuth
      loginWithGoogle: async () => {
        set({ isLoading: true, error: null });

        try {
          await auth.signInWithGoogle();
          // OAuth will redirect, so we don't set loading to false here
        } catch (error) {
          const err = error as Error;
          set({
            isLoading: false,
            error: err.message || 'Google login failed',
          });
          throw error;
        }
      },

      // Handle OAuth callback
      handleAuthCallback: async () => {
        set({ isLoading: true, error: null });

        try {
          const { user } = await auth.handleAuthCallback();
          const mappedUser = mapAuthUser(user);
          const newIsAdmin = checkIsAdmin(mappedUser);

          set({
            user: mappedUser,
            isAuthenticated: true,
            isAdmin: newIsAdmin,
            isLoading: false,
          });

          // Fetch permissions if admin
          if (newIsAdmin) {
            get().fetchPermissions();
          }
        } catch (error) {
          const err = error as Error;
          set({
            isLoading: false,
            error: err.message || 'Authentication failed',
          });
          throw error;
        }
      },

      logout: async () => {
        // Set flag before signOut to prevent initialize() from re-authenticating
        set({ _justLoggedOut: true });

        try {
          await auth.signOut();
        } catch {
          // Ignore logout errors
        }

        // Clear persisted auth data immediately to prevent rehydration race
        localStorage.removeItem('rumio-auth');

        set({
          user: null,
          isAuthenticated: false,
          isAdmin: false,
          permissions: [],
          permissionsLoaded: false,
          error: null,
        });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      clearError: () => set({ error: null }),

      fetchPermissions: async () => {
        const { isAdmin } = get();
        if (!isAdmin) {
          set({ permissions: [], permissionsLoaded: true });
          return;
        }

        try {
          const token = await auth.getAccessToken();
          if (!token) {
            set({ permissions: [], permissionsLoaded: true });
            return;
          }

          const response = await fetch(`${API_URL}/api/admin/me/permissions`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });

          if (response.ok) {
            const data = await response.json();
            set({
              permissions: data.permissions || [],
              permissionsLoaded: true
            });
          } else {
            set({ permissions: [], permissionsLoaded: true });
          }
        } catch (error) {
          console.error('Failed to fetch permissions:', error);
          set({ permissions: [], permissionsLoaded: true });
        }
      },

      hasPermission: (permission: string) => {
        const { permissions } = get();
        return permissions.includes(permission);
      },

      hasAnyPermission: (perms: string[]) => {
        const { permissions } = get();
        return perms.some(p => permissions.includes(p));
      },

      hasAllPermissions: (perms: string[]) => {
        const { permissions } = get();
        return perms.every(p => permissions.includes(p));
      },

      initialize: async () => {
        // Wait for hydration to complete before initializing
        const state = get();
        if (!state._hasHydrated) {
          return;
        }

        // Skip re-authentication if we just logged out (prevents race condition)
        if (state._justLoggedOut) {
          set({ _justLoggedOut: false, isLoading: false });
          return;
        }

        set({ isLoading: true });

        try {
          // Check if we have an active SuperTokens session
          const isAuthed = await auth.isAuthenticated();

          if (isAuthed) {
            // Try to get user profile
            try {
              const token = await auth.getAccessToken();
              if (token) {
                const response = await fetch(`${API_URL}/api/user/me`, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                });

                if (response.ok) {
                  const data = await response.json();
                  // Backend returns { user: {...} } - unwrap if needed
                  const u = data.user || data;
                  const user: User = {
                    id: u.id || u.userId,
                    email: u.email || '',
                    name: u.name,
                    subscription_tier: u.tier || u.subscription_tier || 'free',
                    subscription_source: u.subscription_source,
                    role: u.role,
                    is_admin: u.role !== 'user',
                    referralCode: u.referralCode || u.referral_code,
                    avatarUrl: u.avatarUrl || u.avatar_url,
                    created_at: u.createdAt || u.created_at || new Date().toISOString(),
                  };

                  const newIsAdmin = checkIsAdmin(user);

                  set({
                    user,
                    isAuthenticated: true,
                    isAdmin: newIsAdmin,
                    isLoading: false,
                  });

                  // Fetch permissions if admin
                  if (newIsAdmin) {
                    get().fetchPermissions();
                  }
                  return;
                }
              }
            } catch (error) {
              console.error('Failed to fetch user profile:', error);
            }

            // Session exists but couldn't fetch profile - keep persisted user data temporarily
            // but only if we got a token (confirms session is real, not stale cookies)
            const { user } = get();
            if (user && await auth.getAccessToken()) {
              set({
                isAuthenticated: true,
                isLoading: false,
              });
              return;
            }
          }

          // No active session
          set({
            user: null,
            isAuthenticated: false,
            isAdmin: false,
            isLoading: false,
          });
        } catch (error) {
          console.error('Auth initialization error:', error);
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'rumio-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist minimal data (session is handled by SuperTokens cookies)
        user: state.user,
        permissions: state.permissions,
        permissionsLoaded: state.permissionsLoaded,
      }),
      onRehydrateStorage: () => (state) => {
        // Called when hydration is complete
        if (state) {
          state.setHasHydrated(true);
          // Initialize to check SuperTokens session
          state.initialize();
        }
      },
    }
  )
);

// Export getAccessToken for use in api.ts
export { auth };
